/**
 * Payment Section
 * Displays payment and billing settings
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DcsApp } from '../../types/general-settings-types';
import { Input } from '@/core/components/ui/input';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Label } from '@/core/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { servicesContainer } from '@/core/di/services-container';

interface PaymentSectionProps {
  app: DcsApp;
  onFieldChange: (field: string, value: any) => void;
  hasChanges?: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

export const PaymentSection = ({ app, onFieldChange, hasChanges, isOpen, onToggle }: PaymentSectionProps) => {
  const { t } = useTranslation('assignEv');
  const [currencies, setCurrencies] = useState<Array<{ code: string; name: string }>>([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  // Load currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      setLoadingCurrencies(true);
      try {
        const services = servicesContainer.getServices();
        const currencyList = await services.getCurrenciesUseCase.execute();
        console.log('[PaymentSection] Currencies loaded:', currencyList.length);
        setCurrencies(currencyList.map(c => ({ code: c.code, name: c.name })));
      } catch (error) {
        console.error('[PaymentSection] Failed to load currencies:', error);
      } finally {
        setLoadingCurrencies(false);
      }
    };

    loadCurrencies();
  }, []);

  return (
    <div className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          )}
          <h3 className="font-bold text-gray-900 text-base">{t('general.paymentSection.title')}</h3>
          {hasChanges && (
            <span className="ml-2 px-3 py-1 text-xs font-bold text-white bg-orange-500 rounded-full shadow-md">
              {t('badges.modified')}
            </span>
          )}
        </div>
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="px-6 py-4 bg-white space-y-8">
          {/* ===== PAYMENT TOGGLES ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.paymentSection.subsections.paymentFeatures')}
            </h4>
            <div className="flex gap-6 items-center">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="noBilling"
                  checked={!app.noBilling}
                  onCheckedChange={(checked) =>
                    onFieldChange('noBilling', !checked)
                  }
                />
                <Label htmlFor="noBilling" className="text-sm cursor-pointer">
                  {t('general.paymentSection.fields.enablePayment')}
                </Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="supportEcomSdk"
                  checked={app.supportEcomSDK || false}
                  onCheckedChange={(checked) =>
                    onFieldChange('supportEcomSDK', checked)
                  }
                />
                <Label htmlFor="supportEcomSdk" className="text-sm cursor-pointer">
                  {t('general.paymentSection.fields.supportEcomSdk')}
                </Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="useThreeDs"
                  checked={app.useThreeDs || false}
                  onCheckedChange={(checked) =>
                    onFieldChange('useThreeDs', checked)
                  }
                />
                <Label htmlFor="useThreeDs" className="text-sm cursor-pointer">
                  {t('general.paymentSection.fields.useThreeDs')}
                </Label>
              </div>
            </div>
          </div>

          {/* ===== BILLING CONFIGURATION ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.paymentSection.subsections.billingConfiguration')}
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.billingCycleDay')}
                </Label>
                <Input
                  type="number"
                  value={app.billingCycleDay || ''}
                  onChange={(e) =>
                    onFieldChange('billingCycleDay', parseInt(e.target.value) || 0)
                  }
                  disabled
                  className="bg-gray-100 border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.billingBarrierDays')}
                </Label>
                <Input
                  type="number"
                  value={app.billingBarrierDays || ''}
                  onChange={(e) =>
                    onFieldChange('billingBarrierDays', parseInt(e.target.value) || 0)
                  }
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="govIdRequired" className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.govIdRequired')}
                </Label>
                <div className="flex items-center h-10">
                  <Checkbox
                    id="govIdRequired"
                    checked={false}
                    disabled
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.inDebtDays')}
                </Label>
                <Input
                  type="number"
                  value={app.inDebtDays ?? ''}
                  onChange={(e) =>
                    onFieldChange('inDebtDays', parseInt(e.target.value) || 0)
                  }
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="inDebtEnabled" className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.inDebtEnabled')}
                </Label>
                <div className="flex items-center h-10">
                  <Checkbox
                    id="inDebtEnabled"
                    checked={!!app.inDebtEnabled}
                    onCheckedChange={(checked) => onFieldChange('inDebtEnabled', !!checked)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ===== MACHINES & AUTHENTICATION ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.paymentSection.subsections.machinesAuthentication')}
            </h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.validationMachine')}
                </Label>
                <Input
                  type="number"
                  value={app.validationTerminal || ''}
                  onChange={(e) =>
                    onFieldChange('validationTerminal', parseInt(e.target.value) || 0)
                  }
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.paymentMachine')}
                </Label>
                <Input
                  type="number"
                  value={app.singleChargeTerminal || ''}
                  onChange={(e) =>
                    onFieldChange('singleChargeTerminal', parseInt(e.target.value) || 0)
                  }
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.incrementalAuth')}
                </Label>
                <Select
                  value={app.incrementalAuthType || 'None'}
                  onValueChange={(value) =>
                    onFieldChange('incrementalAuthType', value)
                  }
                >
                  <SelectTrigger className="bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="None">{t('general.paymentSection.fields.incrementalAuthNone')}</SelectItem>
                    <SelectItem value="SingleDuringSettlement">{t('general.paymentSection.fields.incrementalAuthSingleDuringSettlement')}</SelectItem>
                    <SelectItem value="Multiple">{t('general.paymentSection.fields.incrementalAuthMultiple')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.minEnergyConnectionFee')}
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={app.minEnergyForConnectionFee || ''}
                    onChange={(e) =>
                      onFieldChange('minEnergyForConnectionFee', parseFloat(e.target.value) || 0)
                    }
                    className="bg-white border-gray-300"
                  />
                  <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-600 min-w-fit">
                    {t('general.paymentSection.fields.kWhUnit')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== CURRENCY & AMOUNTS ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.paymentSection.subsections.currencyAmounts')}
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.currency')}
                </Label>
                <Select
                  value={app.currency || ''}
                  onValueChange={(value) =>
                    onFieldChange('currency', value)
                  }
                >
                  <SelectTrigger className="bg-white border-gray-300">
                    <SelectValue placeholder={t('general.paymentSection.fields.currencyPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.length > 0 ? (
                      currencies.map((curr) => (
                        <SelectItem key={curr.code} value={curr.code}>
                          {curr.code} - {curr.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-2 text-sm text-gray-500">
                        {loadingCurrencies ? t('general.paymentSection.fields.loadingCurrencies') : t('general.paymentSection.fields.noCurrencies')}
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.minBillingAmount')}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={app.minEngineBillingAmount || ''}
                  onChange={(e) =>
                    onFieldChange('minEngineBillingAmount', parseFloat(e.target.value) || 0)
                  }
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.paymentSection.fields.autoStopCreditDelta')}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={app.autoStopDefaultCreditDeltaAmount || ''}
                  onChange={(e) =>
                    onFieldChange('autoStopDefaultCreditDeltaAmount', parseFloat(e.target.value) || 0)
                  }
                  className="bg-white border-gray-300"
                />
              </div>
            </div>
          </div>

          {/* ===== E-RECEIPT ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.paymentSection.subsections.eReceipt')}
            </h4>
            <div>
              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                {t('general.paymentSection.fields.eReceiptAppId')}
              </Label>
              <Input
                type="number"
                value={app.eReceiptAppId || ''}
                onChange={(e) =>
                  onFieldChange('eReceiptAppId', parseInt(e.target.value) || 0)
                }
                className="bg-white border-gray-300"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
